// MA-181 — ONE AUDIO HOME (ADR-056 D6).
//
// WebSpeechSource is the browser-speechSynthesis implementation of
// NarrationSource. It absorbs the utterance-building logic that used to be
// duplicated between src/engine/audioManager.ts's `_speak` and
// src/components/SpeakPromptButton.tsx's inline `onTap` — this is now the
// single place either caller builds a SpeechSynthesisUtterance.
//
// PHONEME_SAY + preferredVoice are canonical HERE; audioManager.ts
// re-exports them rather than defining its own copies, so there is exactly
// one "audio home" for voice ranking + phoneme pronunciation.
import type { NarrationCapabilities, NarrationPlayOptions, NarrationSource, Unsubscribe } from "./narrationSource";
import { recordNarrationPlay } from "./narrationPlayLedger";
import { SENTENCE_PAUSE_MS, splitIntoSentences } from "./narrationPacing";

// Canonical phoneme -> spoken-sound map. TTS would read "/b/" literally as
// "slash b slash"; this maps tuned phoneme symbols to short pronounceable
// strings (e.g. /b/ -> "buh", /sh/ -> "shh"). Unknown symbols strip slashes
// and fall through (see speakableText in SpeakPromptButton.tsx).
export const PHONEME_SAY: Record<string, string> = {
  // single consonants — voiced/unvoiced stop and fricatives
  "/b/": "buh", "/p/": "puh", "/t/": "tuh", "/d/": "duh",
  "/k/": "kuh", "/g/": "guh", "/m/": "mm", "/n/": "nn",
  "/f/": "fff", "/v/": "vvv", "/s/": "ss", "/z/": "zz",
  "/h/": "huh", "/l/": "lll", "/r/": "rrr", "/w/": "wuh",
  "/y/": "yuh", "/j/": "juh",
  // digraphs
  "/sh/": "shh", "/ch/": "ch", "/th/": "th", "/ng/": "ng",
  // short vowels. MA-667 slice c2 (2026-09-20): /a/ and /o/ used to share
  // one respelling ("ah"), so a "cat" sound-out and a "cot" sound-out were
  // ASR-indistinguishable — acceptance clause 3. Step 4 measured that moving
  // /a/ alone to "aa" changes zero narratable strings once v1 sound-out text
  // leaves the practice+library denominator (the only other reader of this
  // table, SpeakPromptButton's inline `/x/` phoneme prompts, has no /a/
  // callers in the current curriculum seeds); done as a global-table change
  // rather than a shape-scoped override table.
  "/a/": "aa", "/e/": "eh", "/i/": "ih", "/o/": "ah",
  "/u/": "uh",
  // long vowels (uppercase convention used in some seeds)
  "/A/": "ay", "/E/": "ee", "/I/": "eye", "/O/": "oh", "/U/": "you",
  // MA-265 — vowel-team / r-controlled sounds with no single-phoneme
  // equivalent above (consonant digraphs like /wh/, /ph/, /kn/ reuse an
  // existing single-consonant key instead — see decodeUtterance.ts).
  "/qu/": "kwuh", "/ow/": "ow", "/oo/": "oo", "/oi/": "oy", "/au/": "aw",
  "/ar/": "ar", "/or/": "or", "/er/": "er",
  "/air/": "air", "/ear/": "eer", "/ure/": "yoor",
};

export function preferredVoice(synth: SpeechSynthesis): SpeechSynthesisVoice | null {
  const voices = synth.getVoices() || [];
  // Prefer English female voices that sound friendlier for kids;
  // fall back to any English voice; finally any voice.
  const ranked = ["Samantha", "Karen", "Google US English", "Google UK English Female", "Microsoft Aria", "Microsoft Zira"];
  for (const name of ranked) {
    const v = voices.find((vv) => vv.name?.includes(name));
    if (v) return v;
  }
  return voices.find((v) => v.lang?.startsWith("en")) || voices[0] || null;
}

export class WebSpeechSource implements NarrationSource {
  readonly capabilities: NarrationCapabilities = { wordTiming: false };

  private _synth: SpeechSynthesis | null;
  // Holds the most recently created utterance so it can't be garbage
  // collected mid-speech — a known Chromium quirk that silently drops onend.
  private _utterance: SpeechSynthesisUtterance | null = null;
  private _endCbs = new Set<() => void>();
  private _errorCbs = new Set<() => void>();
  private _wordCbs = new Set<(wordIndex: number, charIndex: number) => void>();
  // MA-257 — sentence-chain bookkeeping. `_generation` is bumped by every
  // play()/stop() call; a chained onend/gap-timer callback checks it's
  // still current before advancing, so a stale chain from a superseded
  // play() (tap-a-word firing mid page-narration-gap, or a rapid re-tap)
  // can never speak/schedule again after being superseded.
  private _generation = 0;
  private _pendingGapTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this._synth = typeof window !== "undefined" ? window.speechSynthesis : null;
  }

  isAvailable() {
    return this._synth != null;
  }

  load(_text: string) {
    // No-op: nothing to prefetch for live browser synthesis.
  }

  play(text: string, opts: NarrationPlayOptions = {}) {
    const synth = this._synth;
    if (!synth) return;
    synth.cancel(); // clear any queued/stale speech before starting fresh
    if (this._pendingGapTimer) {
      clearTimeout(this._pendingGapTimer);
      this._pendingGapTimer = null;
    }
    const generation = ++this._generation;
    const segments = opts.pacedPauses ? splitIntoSentences(text) : [text];
    // MA-586 — the play ledger records THIS lane starting (one record per
    // play(), carrying the FULL narrated text even when pacedPauses splits it
    // into sentence utterances). A no-op unless a test opted in.
    recordNarrationPlay({
      source: "webspeech",
      audioId: null,
      audioPath: null,
      text,
      requestedText: text,
      rate: opts.rate ?? null,
      mode: opts.mode ?? null,
    });
    this._speakSegment(synth, segments, 0, generation, opts);
  }

  private _speakSegment(
    synth: SpeechSynthesis,
    segments: string[],
    index: number,
    generation: number,
    opts: NarrationPlayOptions,
  ) {
    if (generation !== this._generation) return; // superseded by a later play()/stop()
    const isLast = index === segments.length - 1;
    const utt = new SpeechSynthesisUtterance(segments[index]);
    const voice = preferredVoice(synth);
    if (voice) utt.voice = voice;
    utt.lang = opts.lang ?? voice?.lang ?? "en-US";
    utt.rate = opts.rate ?? 1;
    utt.pitch = opts.pitch ?? 1;
    utt.onend = () => {
      if (generation !== this._generation) return;
      if (isLast) {
        this._endCbs.forEach((cb) => cb());
        return;
      }
      this._pendingGapTimer = setTimeout(() => {
        this._pendingGapTimer = null;
        this._speakSegment(synth, segments, index + 1, generation, opts);
      }, SENTENCE_PAUSE_MS);
    };
    // A mid-chain error forwards immediately and stops the chain (only
    // `onend` schedules the next segment) — never a silent stall waiting
    // on a caller's own fallback ceiling.
    utt.onerror = () => {
      if (generation !== this._generation) return;
      this._errorCbs.forEach((cb) => cb());
    };
    this._utterance = utt;
    synth.speak(utt);
  }

  stop() {
    if (this._pendingGapTimer) {
      clearTimeout(this._pendingGapTimer);
      this._pendingGapTimer = null;
    }
    this._generation++; // invalidate any in-flight/pending chain
    if (this._synth) this._synth.cancel();
  }

  onEnd(cb: () => void): Unsubscribe {
    this._endCbs.add(cb);
    return () => this._endCbs.delete(cb);
  }

  onError(cb: () => void): Unsubscribe {
    this._errorCbs.add(cb);
    return () => this._errorCbs.delete(cb);
  }

  onWord(cb: (wordIndex: number, charIndex: number) => void): Unsubscribe {
    // capabilities.wordTiming is false — onboundary is deliberately not
    // wired (unreliable, esp. iOS), so this callback is never invoked today.
    this._wordCbs.add(cb);
    return () => this._wordCbs.delete(cb);
  }

  seekToWord(_index: number) {
    // No-op: only meaningful for a wordTiming-capable source.
  }
}

export const webSpeechSource = new WebSpeechSource();
